import { DisabledReason } from '../../platform/access/DisabledReason'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { api } from '../../api/client'
import { useToast } from '../../platform/feedback/ToastProvider'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { Button } from '../../platform/primitives/Button'
import { useBlobDownload } from '../../platform/useBlobDownload'
import { monthBounds } from './filters'
import { monthName } from './txnDisplay'

export const EXPORT_REASON = 'Choose a month to export'
export const EXPORT_FAILED = 'Export failed. Try again.'
const REASON_ID = 'txn-export-reason'
/** Shared by the live buttons and the locked gate's `ButtonPreview` (#578). */
const EXPORT_VARIANT = 'outline'
const EXPORT_SIZE = 'page'

/** "Export July CSV", or "Export July 2025 CSV" outside this year. The label names the PERIOD and nothing else. */
export function exportLabel(month: string | null, today: Date): string {
  return month === null ? 'Export CSV' : `Export ${monthName(month, today)} CSV`
}

/**
 * Export the selected month (Q9), gated on `export_data` and on nothing else.
 *
 * **The endpoint takes a period only**, so the label never promises the list's other filters: a
 * search, an account or a tag does not narrow the file. All time has no period, so it is disabled
 * with the reason beside it rather than silently exporting a year the user did not pick.
 *
 * The file is the server's: no CSV is built here. Its known gaps (the provider's category, and rows
 * the bank has since removed) are expense-monitor-backend issue 466 and are NOT worked around.
 *
 * Unentitled, `PremiumGate` renders the locked preview and no request is ever made. The button
 * keeps focus on failure: it is `aria-disabled` while a download runs, never `disabled`, which would
 * drop focus to the page.
 */
export function ExportButton({ month, today }: { month: string | null; today: Date }) {
  const label = exportLabel(month, today)
  const preview = (
    <ButtonPreview variant={EXPORT_VARIANT} size={EXPORT_SIZE}>
      {label}
    </ButtonPreview>
  )
  return (
    <PremiumGate entitlement="export_data" label={label} preview={preview}>
      {month === null ? (
        <DisabledReason reason={EXPORT_REASON} reasonId={REASON_ID}>
          <Button type="button" size={EXPORT_SIZE} variant={EXPORT_VARIANT} aria-disabled="true" aria-describedby={REASON_ID}>
            {label}
          </Button>
        </DisabledReason>
      ) : (
        <Download month={month} label={label} />
      )}
    </PremiumGate>
  )
}

function Download({ month, label }: { month: string; label: string }) {
  const toast = useToast()
  const { busy, run: download } = useBlobDownload()

  const run = async () => {
    const result = await download(async () => {
      const { start, end } = monthBounds(month)
      const { data, error } = await api.GET('/api/v1/reports/export', {
        params: { query: { start_date: start, end_date: end, format: 'csv' } },
        parseAs: 'blob',
      })
      if (error !== undefined || !(data instanceof Blob)) throw new Error('export-failed')
      return { blob: data, filename: `tracke5-${month}.csv` }
    })
    if (result.status === 'failed') toast.show({ kind: 'error', message: EXPORT_FAILED })
  }

  return (
    <>
      <Button type="button" size={EXPORT_SIZE} variant={EXPORT_VARIANT} aria-busy={busy} aria-disabled={busy} onClick={() => void run()}>
        {busy ? 'Exporting…' : label}
      </Button>
      {/* Present from the start so the change is announced; the focused button's own name change is not reliably spoken. */}
      <span className="sr-only" role="status">
        {busy ? 'Exporting…' : ''}
      </span>
    </>
  )
}
